import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import NotesOutlined from '@mui/icons-material/NotesOutlined'
import { Reply as ReplyIcon } from '@mui/icons-material'
import type { AgentMode, UUID } from '@/index'
import { CommandType, EventType } from '@/index'
import type { TransitionType } from '@/features/events/shared/hooks'
import type {
  ClaudeSession,
  Command,
  ConversationTurn,
  ConversationTurnEntry,
} from '@/features/events/conversation/state/types'
import {
  getLatestConversationTurn,
  getLatestDisplayableEvent,
  getSessionEventsSortedNewestFirst,
  isNotInteractable,
} from '@/features/events/conversation/state/types'
import { useDialog } from '@/common/hooks'
import {
  ConversationDialog,
  MostRecentEventDialog,
  SessionDraftsDialog,
  UserInputDialog,
} from '@/features/events/conversation'
import type { FormattedAgentActivity, FormattedUserActivity } from '@/features/events/activity'
import {
  ActivityFeedBar,
  ActivityStatusIndicator,
  IdleActivityIndicator,
  useLatestActivity,
} from '@/features/events/activity'
import { standardTooltipProps } from '@/common/components'
import { useSessionDialogManager } from '@/features/inbox'
import { useCrypto } from '@/infra/crypto/context'
import {
  buildCommandPayload,
  commandClient,
  encryptCommand,
} from '@/features/events/conversation/controls/api'
import { MostRecentEventPreview, iconColumnSx, staticIconSx } from './MostRecentEventPreview'
import { SessionCardLayout } from './SessionCardLayout'
import {
  conversationButtonDisabledSx,
  conversationButtonSx,
  conversationIconSx,
  replyIconButtonSx,
  replyIconSizeSx,
  transientMessageSx,
  transientPreviewRowSx,
} from './sessionCardStyles'

interface SessionEventsOrchestratorProps {
  readonly session: ClaudeSession
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly createNewTurn: (session: ClaudeSession, userPromptId: UUID, userCommand: Command) => void
  readonly isTransitioning?: boolean | undefined
  readonly transitionType?: TransitionType | null | undefined
  readonly onResumeSession?: ((session: ClaudeSession) => void) | undefined
}

export const SessionEventsOrchestrator: FC<SessionEventsOrchestratorProps> = ({
  session,
  recordCommand,
  createNewTurn,
  isTransitioning = false,
  transitionType = null,
  onResumeSession,
}) => {
  const { displayableLatestEvent, latestConversationTurn } = useSessionData(session)
  const { activity } = useLatestActivity({
    sessionId: session.sessionId,
    initialActivity: session.runtime.latestActivity,
  })

  const mostRecentEventDialog = useDialog()
  const conversationDialog = useDialog()
  const draftsDialog = useDialog()
  const newMessageDialog = useDialog()

  const { registerSession, unregisterSession, registerSessionDrafts, unregisterSessionDrafts } =
    useSessionDialogManager()

  // Forward the latest open fns through refs so the registered callbacks always
  // invoke the current dialog opener without re-registering on every render.
  const openFnRef = useRef(mostRecentEventDialog.open)
  useEffect(() => {
    openFnRef.current = mostRecentEventDialog.open
  })

  const openDraftsFnRef = useRef(draftsDialog.open)
  useEffect(() => {
    openDraftsFnRef.current = draftsDialog.open
  })

  useEffect(() => {
    const sessionId = session.sessionId
    registerSession(sessionId, () => openFnRef.current())
    registerSessionDrafts(sessionId, () => openDraftsFnRef.current())
    return () => {
      unregisterSession(sessionId)
      unregisterSessionDrafts(sessionId)
    }
  }, [
    session.sessionId,
    registerSession,
    unregisterSession,
    registerSessionDrafts,
    unregisterSessionDrafts,
  ])

  const newMessageSubmission = useNewMessageSubmission({ session, createNewTurn })

  const handleOpenConversationFromEvent = useCallback(() => {
    mostRecentEventDialog.close()
    conversationDialog.open()
  }, [mostRecentEventDialog, conversationDialog])

  const userReplyText = useMemo(() => {
    if (!latestConversationTurn) return null
    const latestCommand = latestConversationTurn.userCommands.at(-1)
    return latestCommand?.userPrompt ?? null
  }, [latestConversationTurn])

  const handleNewMessageSubmit = useCallback(
    async (prompt: string, agentMode?: AgentMode | null): Promise<boolean> => {
      const success = await newMessageSubmission.submit(prompt, agentMode ?? undefined)
      if (success) {
        newMessageDialog.close()
      }
      return success
    },
    [newMessageSubmission, newMessageDialog],
  )

  const hasActivity = activity !== null && activity.status !== 'idle'
  const isProcessingEvent = isProcessingEventType(displayableLatestEvent?.eventType ?? null)
  const isProcessing = hasActivity || isProcessingEvent

  const canShowConversationButton = latestConversationTurn !== null || session.isTransient
  const isInteractable = !isNotInteractable(session)
  const showReplyIcon = isInteractable && displayableLatestEvent === null && !session.isTransient

  const newMessageDialogProps = {
    open: newMessageDialog.isOpen,
    onClose: newMessageDialog.close,
    onSubmit: handleNewMessageSubmit,
    session,
    isSubmitting: newMessageSubmission.isSubmitting,
    error: newMessageSubmission.error,
    draftType: 'user_reply' as const,
    refEventId: null,
    title: 'New Message',
    placeholder: 'Send a message to Claude Code...',
    description: 'Start a new conversation with this session',
    currentAgentMode: session.currentAgentMode,
    dangerouslySkipPermissions: session.dangerouslySkipPermissions,
    autoModeSupported: session.autoModeSupported,
    submitButtonLabel: 'Send',
    submitButtonLoadingLabel: 'Sending...',
  }

  const handleResumeClick = useCallback(() => {
    onResumeSession?.(session)
  }, [onResumeSession, session])

  const conversationSlot = canShowConversationButton ? (
    <Tooltip
      {...standardTooltipProps}
      title={session.isTransient ? 'No conversation yet' : 'View conversation timeline'}
    >
      <span>
        <IconButton
          onClick={session.isTransient ? undefined : conversationDialog.open}
          disabled={session.isTransient}
          size="small"
          sx={session.isTransient ? conversationButtonDisabledSx : conversationButtonSx}
          aria-label="View conversation timeline"
        >
          <NotesOutlined sx={conversationIconSx} />
        </IconButton>
      </span>
    </Tooltip>
  ) : undefined

  const previewSlot = displayableLatestEvent ? (
    <MostRecentEventPreview
      event={displayableLatestEvent}
      isProcessing={isProcessing}
      onClick={mostRecentEventDialog.open}
      userActivity={session.runtime.userActivity}
      userReplyText={userReplyText}
    />
  ) : session.isTransient ? (
    <Box sx={transientPreviewRowSx} onClick={newMessageDialog.open}>
      <Box sx={iconColumnSx}>
        <ReplyIcon sx={staticIconSx('success.main')} />
      </Box>
      <Tooltip {...standardTooltipProps} title="Send a message" followCursor>
        <Typography variant="body2" sx={transientMessageSx}>
          Prompt Claude Code to kick off the conversation
        </Typography>
      </Tooltip>
    </Box>
  ) : undefined

  const replySlot = showReplyIcon ? (
    <Tooltip {...standardTooltipProps} title="Send message">
      <IconButton
        onClick={newMessageDialog.open}
        size="small"
        sx={replyIconButtonSx}
        aria-label="Send message to session"
      >
        <ReplyIcon sx={replyIconSizeSx} />
      </IconButton>
    </Tooltip>
  ) : undefined

  return (
    <>
      <SessionCardLayout
        conversation={conversationSlot}
        preview={previewSlot}
        reply={replySlot}
        activity={renderActivityState(
          hasActivity,
          isTransitioning,
          transitionType,
          session,
          activity,
        )}
        isTransitioning={isTransitioning}
      />

      {displayableLatestEvent && (
        <MostRecentEventDialog
          open={mostRecentEventDialog.isOpen}
          onClose={mostRecentEventDialog.close}
          event={displayableLatestEvent}
          session={session}
          latestEvent={displayableLatestEvent}
          conversationTurn={latestConversationTurn}
          recordCommand={recordCommand}
          createNewTurn={createNewTurn}
          onOpenConversation={handleOpenConversationFromEvent}
          onResumeSession={onResumeSession ? handleResumeClick : undefined}
          isTransitioning={isTransitioning}
        />
      )}

      {latestConversationTurn && (
        <ConversationDialog
          open={conversationDialog.isOpen}
          onClose={conversationDialog.close}
          session={session}
          userPromptId={latestConversationTurn.userPromptId}
          recordCommand={recordCommand}
          createNewTurn={createNewTurn}
        />
      )}

      <SessionDraftsDialog
        open={draftsDialog.isOpen}
        onClose={draftsDialog.close}
        session={session}
        recordCommand={recordCommand}
        createNewTurn={createNewTurn}
      />

      <UserInputDialog {...newMessageDialogProps} />
    </>
  )
}

interface SessionData {
  readonly displayableLatestEvent: ConversationTurnEntry | null
  readonly latestConversationTurn: ConversationTurn | null
}

function useSessionData(session: ClaudeSession): SessionData {
  const displayableLatestEvent = getLatestDisplayableEvent(session)

  const latestConversationTurn = useMemo(() => getLatestConversationTurn(session), [session])

  return {
    displayableLatestEvent,
    latestConversationTurn,
  }
}

interface NewMessageSubmissionState {
  readonly isSubmitting: boolean
  readonly error: string | null
  readonly submit: (prompt: string, agentMode?: AgentMode) => Promise<boolean>
}

interface UseNewMessageSubmissionParams {
  readonly session: ClaudeSession
  readonly createNewTurn: (session: ClaudeSession, userPromptId: UUID, userCommand: Command) => void
}

function useNewMessageSubmission(params: UseNewMessageSubmissionParams): NewMessageSubmissionState {
  const { session, createNewTurn } = params
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { encrypt, activeKeyVersion, isReady } = useCrypto()

  const submit = useCallback(
    async (prompt: string, agentMode?: AgentMode): Promise<boolean> => {
      if (isNotInteractable(session)) {
        setError('Session is not interactable. No active tmux session.')
        return false
      }

      if (!isReady || activeKeyVersion === null) {
        setError('Encryption is not available. Cannot send message.')
        return false
      }

      setIsSubmitting(true)
      setError(null)

      try {
        const newUserPromptId = crypto.randomUUID()
        const latestEventId = getLatestEventId(session)
        const userCommand: Command = {
          commandType: CommandType.SUBMIT_PROMPT,
          userCommandId: crypto.randomUUID(),
          refEventId: latestEventId,
          timestamp: new Date().toISOString(),
          userOption: null,
          userPrompt: prompt,
          agentMode: agentMode ?? null,
          userAnswers: null,
          errorRecovery: null,
        }

        const payload = buildCommandPayload(userCommand, session, null, newUserPromptId)
        const encryptedPayload = await encryptCommand(payload, activeKeyVersion, encrypt)
        const result = await commandClient.submit(encryptedPayload)

        if (!result.success) {
          setError(result.message)
          return false
        }

        createNewTurn(session, newUserPromptId, userCommand)
        return true
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred'
        setError(message)
        return false
      } finally {
        setIsSubmitting(false)
      }
    },
    [session, createNewTurn, encrypt, activeKeyVersion, isReady],
  )

  return { isSubmitting, error, submit }
}

function getLatestEventId(session: ClaudeSession): string {
  const allSessionEvents = getSessionEventsSortedNewestFirst(session)
  const latestEvent = allSessionEvents[0]
  if (!latestEvent) {
    return session.sessionId
  }
  return latestEvent.eventId
}

function isProcessingEventType(eventType: EventType | null): boolean {
  if (eventType === null) return false
  return eventType === EventType.USER_PROMPT || eventType === EventType.AGENT_REPLY
}

function renderActivityState(
  hasActivity: boolean,
  isTransitioning: boolean,
  transitionType: TransitionType | null,
  session: ClaudeSession,
  activity: FormattedAgentActivity | FormattedUserActivity | null,
) {
  if (hasActivity) {
    return <ActivityFeedBar sessionId={session.sessionId} latestActivity={activity} />
  }

  if (isTransitioning) {
    return (
      <Box sx={transitioningActivityContainerSx}>
        <Box sx={transitioningActivityIconSlotSx}>
          <ActivityStatusIndicator status="pre_tool" />
        </Box>
        <Typography sx={transitioningActivityTextSx}>
          {transitionType === 'terminating' ? 'Terminating...' : 'Resuming...'}
        </Typography>
      </Box>
    )
  }

  return <IdleActivityIndicator />
}

const transitioningActivityContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const transitioningActivityIconSlotSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  flexShrink: 0,
}

const transitioningActivityTextSx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  fontStyle: 'italic',
  color: 'text.disabled',
}
